import { useContext, useLayoutEffect } from "react";
import {
  ChromeContext,
  type DesktopChrome,
} from "@/app/layouts/desktop/ChromeContext";

/**
 * 各 page が DesktopLayout の chrome (toolbar / page label / sidebar 表示) を
 * 宣言的に流し込むためのフック。
 *
 * mobile では `ChromeContext.Provider` が mount されないため defaultValue の
 * noop `setChrome` が呼ばれ、page は安全に device 判定なしで呼べる。
 *
 * @example
 *   function CatalogPage() {
 *     useDesktopChrome({
 *       toolbar: <CatalogToolbar />,
 *       pageLabel: "カタログ",
 *     });
 *     return <CatalogContent />;
 *   }
 */
export function useDesktopChrome(chrome: DesktopChrome): void {
  const { setChrome } = useContext(ChromeContext);

  // 各 chrome 変更時の適用。chrome (page が毎回新規 object を渡す前提) が変わるたびに
  // 再 set。cleanup を「次の値が来る前に空に戻す」往復にすると親 state 更新が倍になり、
  // 不要な再描画と chrome 入れ替え時の一瞬の空状態を生むので、ここでは空クリアしない。
  useLayoutEffect(() => {
    setChrome(chrome);
  }, [chrome, setChrome]);

  // ページ unmount 時の最終クリア。安定参照の setChrome のみに依存させ、
  // chrome 変更ごとにこの effect を発火させない。
  useLayoutEffect(() => {
    return (): void => setChrome({});
  }, [setChrome]);
}
